import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Box, Sparkles } from "lucide-react";
import Logo from "../../shared/brand/Logo";
import { getShowcase, type ShowcaseData } from "../../features/vault/vaultApi";

/** Public per-workspace portfolio (no auth). Grid of opt-in models. */
export default function ShowcasePage() {
  const { slug } = useParams<{ slug: string }>();
  const [data, setData] = useState<ShowcaseData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    getShowcase(slug!)
      .then((d) => alive && setData(d))
      .catch((e) => alive && setError(e instanceof Error ? e.message : "Not found"));
    return () => {
      alive = false;
    };
  }, [slug]);

  if (error)
    return (
      <div className="flex h-screen items-center justify-center bg-canvas text-ink-400">
        <p className="text-[14px]">{error}</p>
      </div>
    );
  if (!data)
    return (
      <div className="flex h-screen items-center justify-center bg-canvas">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-gold-400 border-t-transparent" />
      </div>
    );

  return (
    <div className="vault-surface min-h-screen bg-canvas text-ink-900">
      {/* Profile header */}
      <header className="nav-marble border-b border-ink-100">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-5">
          <div className="flex items-center gap-3.5">
            {data.workspace.logo ? (
              <img src={data.workspace.logo} alt="" className="h-11 w-11 rounded-xl object-cover" />
            ) : (
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-gold-100 to-gold-50 text-[16px] font-bold text-gold-700 border border-gold-200/60">
                {data.workspace.name.slice(0, 1).toUpperCase()}
              </div>
            )}
            <div>
              <h1 className="font-headline text-[22px] leading-tight text-ink-900">{data.workspace.name}</h1>
              <p className="text-[12px] text-ink-400">Collection · {data.models.length} pieces</p>
            </div>
          </div>
          <Logo size="xs" showWordmark={false} />
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-8">
        {data.models.length === 0 ? (
          <div className="flex h-[50vh] flex-col items-center justify-center text-center text-ink-400">
            <Box size={28} />
            <p className="mt-3 text-[14px]">No published pieces yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-4">
            {data.models.map((m, i) => (
              <motion.div
                key={m.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
              >
                <Link
                  to={`/showcase/${slug}/${m.id}`}
                  className="group block overflow-hidden rounded-2xl border border-ink-100 bg-white transition-shadow hover:shadow-soft"
                >
                  <div className="relative flex aspect-square items-center justify-center bg-gradient-to-br from-ink-50 to-canvas">
                    {m.posterUrl ? (
                      <img src={m.posterUrl} alt={m.name} className="h-full w-full object-cover" />
                    ) : (
                      <Box size={30} className="text-ink-300" />
                    )}
                    {m.configurable && (
                      <span className="absolute left-2.5 top-2.5 flex items-center gap-1 rounded-full bg-ink-900/80 px-2 py-0.5 text-[10px] font-medium text-white backdrop-blur">
                        <Sparkles size={10} /> Customizable
                      </span>
                    )}
                  </div>
                  <div className="p-3.5">
                    <p className="truncate text-[14px] font-semibold text-ink-900">{m.name}</p>
                    {m.category && <p className="mt-0.5 text-[12px] text-ink-400">{m.category}</p>}
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </main>

      <footer className="border-t border-ink-100 py-6 text-center">
        <a href="/" className="text-[11.5px] text-ink-400 hover:text-ink-700">
          Powered by Jewelitt Vault
        </a>
      </footer>
    </div>
  );
}
